/**
 * 로컬 시드 데이터 — Supabase 환경변수가 없을 때(Demo mode) 이 데이터로 앱이 동작한다.
 * 내용은 기획 문서 / 디자인 프로토타입에 나온 예시 그대로 (오징어 게임 1화, BTS 봄날 등).
 *
 * ⚠️ 실서비스 반영 전 확인 필요:
 *   - 유튜브 공식 클립의 임베드 허용 여부
 *   - 자막·가사 인용 범위 (저작권)
 *   - 영상 파일 자체 호스팅은 금지 — IFrame 임베드만 사용
 */
import type {
  LineWord,
  QuizQuestion,
  SavedWord,
  SubtitleLine,
  Video,
  Word,
} from '../lib/types'

/**
 * ⚠️ TODO(content): 실제 큐레이션 시 검수된 공식 클립 ID로 교체할 것.
 * 임베드가 막힌 영상이면 플레이어가 "Open on YouTube" 카드로 대체된다.
 */
const YT_SPRING_DAY = 'xEeFrLSkMm8'

export const VIDEOS: Video[] = [
  {
    id: 'vid-squid-ep1',
    // 라이선스 확인 전이라 임베드 ID 없음 → 데모용 시뮬레이션 클럭으로 재생된다.
    youtubeId: null,
    title: '오징어 게임 1화 · 무궁화 꽃이',
    titleEn: 'Squid Game Ep.1 · official clip',
    genre: 'drama',
    topikLevel: 3,
    durationSec: 36,
    hasManualCc: true,
    status: 'published',
    newWordCount: 12,
    vocabCoverage: null,
    slug: 'squid-game-mugunghwa',
  },
  {
    id: 'vid-cafe-order',
    youtubeId: null,
    title: '카페에서 주문하기 · 일상 회화',
    titleEn: 'Ordering at a café · everyday Korean',
    genre: 'variety',
    topikLevel: 1,
    durationSec: 22,
    hasManualCc: true,
    status: 'published',
    newWordCount: 6,
    vocabCoverage: null,
    slug: 'cafe-ordering-basics',
  },
  {
    id: 'vid-bts-spring-day',
    youtubeId: YT_SPRING_DAY,
    title: 'BTS — 봄날 (Spring Day)',
    titleEn: 'BTS — Spring Day · official MV',
    genre: 'kpop',
    topikLevel: 3,
    durationSec: 274,
    hasManualCc: false,
    status: 'published',
    newWordCount: 9,
    vocabCoverage: null,
    slug: 'bts-spring-day',
  },
  {
    id: 'vid-office-drama',
    youtubeId: null,
    title: '오피스 드라마 · 거절과 부탁',
    titleEn: 'Office drama · declining and requesting',
    genre: 'drama',
    topikLevel: 4,
    durationSec: 302,
    hasManualCc: true,
    status: 'published',
    newWordCount: 14,
    vocabCoverage: null,
    slug: 'office-drama-requests',
  },
]

export const WORDS: Word[] = [
  { id: 1, lemma: '무궁화', meaningEn: 'Rose of Sharon (national flower)', topikLevel: 4, register: 'formal', pronunciation: null, examForm: null, tip: null, meanings: { ja: 'ムクゲ（韓国の国花）', zh: '木槿花（韩国国花）', vi: 'Hoa dâm bụt (quốc hoa Hàn Quốc)' } },
  { id: 2, lemma: '꽃', meaningEn: "Flower (plant's bloom)", topikLevel: 3, register: 'formal', pronunciation: '꼳', examForm: null, tip: null, meanings: { ja: '花', zh: '花', vi: 'Hoa' } },
  { id: 3, lemma: '피다', meaningEn: 'To bloom, to blossom', topikLevel: 3, register: 'formal', pronunciation: null, examForm: null, tip: null },
  {
    id: 4,
    lemma: '죽다',
    meaningEn: 'To die',
    topikLevel: 2,
    register: 'casual',
    pronunciation: '죽따',
    examForm: '죽습니다 / 죽을 것이다',
    tip: '‘죽어’는 반말 구어체예요. TOPIK 쓰기에서는 ‘죽습니다 / 죽을 것이다’처럼 격식체로 바꿔 써야 감점되지 않아요.',
  },
  { id: 5, lemma: '게임', meaningEn: 'Game', topikLevel: 1, register: 'formal', pronunciation: null, examForm: null, tip: null, meanings: { ja: 'ゲーム', zh: '游戏', vi: 'Trò chơi' } },
  {
    id: 6,
    lemma: '시작하다',
    meaningEn: 'To start, To begin',
    topikLevel: 2,
    register: 'formal',
    pronunciation: '시자카다',
    examForm: null,
    tip: '‘시작합니다’는 격식체예요. 친구에게는 ‘시작해’라고 합니다.',
    example: {
      sentence: '지금부터 시험을 시작하겠습니다.',
      translationEn: 'The exam will now begin.',
    },
  },
  {
    id: 7,
    lemma: '약속',
    meaningEn: 'Promise',
    topikLevel: 1,
    register: 'casual',
    pronunciation: '약쏙',
    examForm: '약속합시다 / 약속하겠습니다',
    tip: '‘약속해!’는 친구 사이 반말이에요. 시험 답안에서는 ‘약속합시다 / 약속하겠습니다’로 씁니다.',
    example: {
      sentence: '두 사람은 다음 주에 만나기로 약속했습니다.',
      translationEn: 'The two promised to meet next week.',
    },
  },
  { id: 8, lemma: '내일', meaningEn: 'Tomorrow', topikLevel: 1, register: 'formal', pronunciation: null, examForm: null, tip: null, meanings: { ja: '明日', zh: '明天', vi: 'Ngày mai' } },
  { id: 9, lemma: '만나다', meaningEn: 'To meet', topikLevel: 1, register: 'formal', pronunciation: null, examForm: null, tip: null, meanings: { ja: '会う', zh: '见面', vi: 'Gặp' } },
  { id: 10, lemma: '마지막', meaningEn: 'The last, final', topikLevel: 2, register: 'formal', pronunciation: null, examForm: null, tip: null },
  {
    id: 11,
    lemma: '기회',
    meaningEn: 'Opportunity, Chance',
    topikLevel: 3,
    register: 'casual',
    pronunciation: null,
    examForm: '기회입니다',
    tip: '‘-야’는 반말 종결어미입니다. 격식체는 ‘기회입니다’.',
    example: {
      sentence: '이번 대회는 좋은 기회입니다.',
      translationEn: 'This competition is a good opportunity.',
    },
  },
  {
    id: 12,
    lemma: '조용히',
    meaningEn: 'Quietly',
    topikLevel: 2,
    register: 'casual',
    pronunciation: null,
    examForm: '조용히 해 주십시오',
    tip: '‘조용히 해’는 반말 명령이에요. 존댓말은 ‘조용히 해 주세요’, 격식체는 ‘조용히 해 주십시오’입니다.',
  },
  {
    id: 13,
    lemma: '움직이다',
    meaningEn: 'To move',
    topikLevel: 2,
    register: 'casual',
    pronunciation: '움지기다',
    examForm: '움직이면 안 됩니다',
    tip: '‘움직이면 안 돼’는 반말이에요. 시험 답안에서는 ‘움직이면 안 됩니다’로 씁니다.',
  },
  {
    id: 14,
    lemma: '살다',
    meaningEn: 'To live, to survive',
    topikLevel: 1,
    register: 'formal',
    pronunciation: null,
    examForm: null,
    tip: '‘살아서 돌아갑시다’의 ‘-ㅂ시다’는 격식을 갖춘 청유형이에요. 친구에게는 ‘살아서 돌아가자’라고 합니다.',
  },
  { id: 15, lemma: '돌아가다', meaningEn: 'To return, to go back', topikLevel: 2, register: 'formal', pronunciation: null, examForm: null, tip: null },
  {
    id: 16,
    lemma: '포기하다',
    meaningEn: 'To give up',
    topikLevel: 4,
    register: 'casual',
    pronunciation: null,
    examForm: '포기하지 않겠습니다',
    tip: '‘포기하지 않을 거야’는 반말이에요. 시험 답안에서는 ‘포기하지 않겠습니다’로 씁니다.',
  },
  { id: 17, lemma: '아메리카노', meaningEn: 'Americano (coffee)', topikLevel: 1, register: 'formal', pronunciation: null, examForm: null, tip: null, meanings: { ja: 'アメリカーノ', zh: '美式咖啡', vi: 'Cà phê Americano' } },
  {
    id: 18,
    lemma: '주다',
    meaningEn: 'To give — “주세요” = please give me',
    topikLevel: 1,
    register: 'formal',
    pronunciation: null,
    examForm: null,
    tip: '‘주세요’는 부드러운 요청이에요. 친구에게는 ‘줘’, 아주 격식 있는 자리에서는 ‘주십시오’를 씁니다.',
  },
  { id: 19, lemma: '따뜻하다', meaningEn: 'To be warm', topikLevel: 1, register: 'formal', pronunciation: '따뜨타다', examForm: null, tip: null },
  {
    id: 20,
    lemma: '얼마',
    meaningEn: 'How much',
    topikLevel: 1,
    register: 'formal',
    pronunciation: null,
    examForm: null,
    tip: '가격을 물을 때는 ‘얼마예요?’, 격식을 갖추면 ‘얼마입니까?’입니다.',
  },
  { id: 21, lemma: '카드', meaningEn: 'Card (credit/debit)', topikLevel: 1, register: 'formal', pronunciation: null, examForm: null, tip: null, meanings: { ja: 'カード', zh: '卡', vi: 'Thẻ' } },
  {
    id: 22,
    lemma: '감사하다',
    meaningEn: 'To be thankful',
    topikLevel: 1,
    register: 'formal',
    pronunciation: null,
    examForm: null,
    tip: '친구에게는 ‘고마워’라고 해요. ‘감사합니다’는 격식을 갖춘 표현이라 시험과 공식적인 자리에 씁니다.',
  },
]

export const SUBTITLE_LINES: SubtitleLine[] = [
  // --- 오징어 게임 1화 클립 (프로토타입 LINES 그대로) ---
  { id: 101, videoId: 'vid-squid-ep1', idx: 0, startSec: 0, endSec: 4.2, text: '무궁화 꽃이 피었습니다.', textEn: 'The hibiscus flower has bloomed.', translations: { ja: 'ムクゲの花が咲きました', zh: '木槿花开了', vi: 'Hoa dâm bụt đã nở' } },
  { id: 102, videoId: 'vid-squid-ep1', idx: 1, startSec: 4.2, endSec: 8, text: '이러다가는 다 죽어!', textEn: "At this rate, we'll all die!", translations: { ja: 'このままじゃ みんな死ぬ！', zh: '再这样下去都得死！', vi: 'Cứ thế này thì chết hết!' } },
  { id: 103, videoId: 'vid-squid-ep1', idx: 2, startSec: 8, endSec: 12, text: '이제 게임을 시작합니다.', textEn: 'Now, the game begins.', translations: { ja: 'では ゲームを始めます', zh: '现在开始游戏', vi: 'Bây giờ bắt đầu trò chơi' } },
  { id: 104, videoId: 'vid-squid-ep1', idx: 3, startSec: 12, endSec: 16.5, text: '우리 내일 만나는 거 약속해!', textEn: "Promise me we'll meet tomorrow!", translations: { ja: '明日 会うって約束して！', zh: '说好明天见！', vi: 'Hứa là mai gặp nhau nhé!' } },
  { id: 105, videoId: 'vid-squid-ep1', idx: 4, startSec: 16.5, endSec: 21, text: '이번이 마지막 기회야.', textEn: 'This is the last chance.' },
  { id: 106, videoId: 'vid-squid-ep1', idx: 5, startSec: 21, endSec: 26, text: '조용히 해, 지금 움직이면 안 돼.', textEn: 'Be quiet — you must not move now.' },
  { id: 107, videoId: 'vid-squid-ep1', idx: 6, startSec: 26, endSec: 31, text: '다들 살아서 돌아갑시다.', textEn: "Let's all go back alive." },
  { id: 108, videoId: 'vid-squid-ep1', idx: 7, startSec: 31, endSec: 36, text: '나는 여기서 포기하지 않을 거야.', textEn: "I'm not giving up here." },

  // --- 카페에서 주문하기 (초급 1급 샘플) ---
  { id: 201, videoId: 'vid-cafe-order', idx: 0, startSec: 0, endSec: 4, text: '아메리카노 한 잔 주세요.', textEn: 'One americano, please.', translations: { ja: 'アメリカーノを一杯ください', zh: '请给我一杯美式咖啡', vi: 'Cho tôi một ly Americano' } },
  { id: 202, videoId: 'vid-cafe-order', idx: 1, startSec: 4, endSec: 9, text: '따뜻한 걸로 드릴까요?', textEn: 'Would you like it hot?' },
  { id: 203, videoId: 'vid-cafe-order', idx: 2, startSec: 9, endSec: 13.5, text: '네, 따뜻한 걸로 주세요.', textEn: 'Yes, hot please.' },
  { id: 204, videoId: 'vid-cafe-order', idx: 3, startSec: 13.5, endSec: 18, text: '전부 얼마예요?', textEn: 'How much is it in total?' },
  { id: 205, videoId: 'vid-cafe-order', idx: 4, startSec: 18, endSec: 22, text: '카드로 계산할게요. 감사합니다.', textEn: "I'll pay by card. Thank you." },
]

/** 자막 토큰(활용형) ↔ 사전 매핑. 어드민 AI 태깅 결과에 해당한다. */
export const LINE_WORDS: LineWord[] = [
  { lineId: 101, token: '무궁화', wordId: 1 },
  { lineId: 101, token: '꽃이', wordId: 2 },
  { lineId: 101, token: '피었습니다', wordId: 3 },
  { lineId: 102, token: '죽어', wordId: 4 },
  { lineId: 103, token: '게임을', wordId: 5 },
  { lineId: 103, token: '시작합니다', wordId: 6 },
  { lineId: 104, token: '내일', wordId: 8 },
  { lineId: 104, token: '만나는', wordId: 9 },
  { lineId: 104, token: '약속해', wordId: 7 },
  { lineId: 105, token: '마지막', wordId: 10 },
  { lineId: 105, token: '기회야', wordId: 11 },
  { lineId: 106, token: '조용히', wordId: 12 },
  { lineId: 106, token: '움직이면', wordId: 13 },
  { lineId: 107, token: '살아서', wordId: 14 },
  { lineId: 107, token: '돌아갑시다', wordId: 15 },
  { lineId: 108, token: '포기하지', wordId: 16 },

  { lineId: 201, token: '아메리카노', wordId: 17 },
  { lineId: 201, token: '주세요', wordId: 18 },
  { lineId: 202, token: '따뜻한', wordId: 19 },
  { lineId: 203, token: '따뜻한', wordId: 19 },
  { lineId: 203, token: '주세요', wordId: 18 },
  { lineId: 204, token: '얼마예요', wordId: 20 },
  { lineId: 205, token: '카드로', wordId: 21 },
  { lineId: 205, token: '감사합니다', wordId: 22 },
]

export const QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    id: 1,
    tag: 'Q1 · K-POP LYRICS',
    source: 'BTS — 봄날 (Spring Day)',
    prompt: '보고 싶다, 얼마나 ___ 봐야',
    promptEn: '“I miss you — how long must I ___ …”',
    options: [
      { ko: '기다려', en: 'to wait' },
      { ko: '먹어', en: 'to eat' },
      { ko: '웃어', en: 'to laugh' },
      { ko: '앉아', en: 'to sit' },
    ],
    answerIdx: 0,
    weightLevel: 3,
  },
  {
    id: 2,
    tag: 'Q2 · DRAMA SCENE',
    source: '오징어 게임 1화 클립',
    prompt: '이제 게임을 ___.',
    promptEn: '“Now, the game ___.”',
    options: [
      { ko: '끝냅니다', en: 'ends' },
      { ko: '시작합니다', en: 'begins' },
      { ko: '팝니다', en: 'sells' },
      { ko: '엽니다', en: 'opens' },
    ],
    answerIdx: 1,
    weightLevel: 2,
  },
  {
    id: 3,
    tag: 'Q3 · SITUATION',
    source: '카페에서 · At a café',
    prompt: '“아메리카노 한 잔 ___.”',
    promptEn: 'Ordering one americano',
    options: [
      { ko: '주세요', en: 'please give me' },
      { ko: '갑니다', en: 'I go' },
      { ko: '봅니다', en: 'I see' },
      { ko: '삽니다', en: 'I buy' },
    ],
    answerIdx: 0,
    weightLevel: 1,
  },
  {
    id: 4,
    tag: 'Q4 · OFFICE DRAMA',
    source: '미생 스타일 오피스 대사',
    prompt: '죄송하지만 이번 건은 ___ 어렵습니다.',
    promptEn: '“Sorry, but this one is difficult to ___.”',
    options: [
      { ko: '맡기', en: 'take on' },
      { ko: '자기', en: 'sleep' },
      { ko: '놀기', en: 'play' },
      { ko: '쉬기', en: 'rest' },
    ],
    answerIdx: 0,
    weightLevel: 4,
  },
  {
    id: 5,
    tag: 'Q5 · IDIOM',
    source: '사극 대사 · Historical drama',
    prompt: '이 일은 ___ 뒤에 논하자.',
    promptEn: '“Let’s discuss this after ___.”',
    options: [
      { ko: '밥 먹은', en: 'eating' },
      { ko: '숙고한', en: 'deliberating' },
      { ko: '뛰어간', en: 'running' },
      { ko: '잠든', en: 'falling asleep' },
    ],
    answerIdx: 1,
    weightLevel: 5,
  },
]

/** 디자인 시안의 시드 단어장 3개 */
export const SEED_SAVED_WORDS: SavedWord[] = [
  {
    id: 'seed-1',
    wordId: 7,
    lemma: '약속',
    meaningEn: 'Promise',
    topikLevel: 1,
    sentence: '우리 내일 만나는 거 약속해!',
    videoId: 'vid-squid-ep1',
    youtubeId: null,
    lineId:104,
    startSec: 12,
    endSec: null,
    mastered: false,
    createdAt: '2026-08-01T00:00:00.000Z',
  },
  {
    id: 'seed-2',
    wordId: 11,
    lemma: '기회',
    meaningEn: 'Opportunity, Chance',
    topikLevel: 3,
    sentence: '이번이 마지막 기회야.',
    videoId: 'vid-squid-ep1',
    youtubeId: null,
    lineId:105,
    startSec: 16.5,
    endSec: null,
    mastered: false,
    createdAt: '2026-08-01T00:01:00.000Z',
  },
  {
    id: 'seed-3',
    wordId: 6,
    lemma: '시작하다',
    meaningEn: 'To start, To begin',
    topikLevel: 2,
    sentence: '이제 게임을 시작합니다.',
    videoId: 'vid-squid-ep1',
    youtubeId: null,
    lineId:103,
    startSec: 8,
    endSec: null,
    mastered: true,
    createdAt: '2026-08-01T00:02:00.000Z',
  },
]
